import { CloseOutlined } from '@ant-design/icons';
import useAuth from '@hooks/use-auth';
import useDocumentTitle from '@hooks/use-document-title';
import { dismissSessionNotice, getSessionNoticeKey, isSessionNoticeDismissed } from '@utils/session-notice-dismissals';
import { Button, ConfigProvider, Layout, Menu, message, Space } from 'antd';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import styles from './Management.module.scss';

type MenuItem = { label: string; to: string } | { href: string; label: string };

const REFERRAL_PROGRAM_NAV_URL =
  'https://supportontelegram.com/referral-program?utm_source=customer_portal&utm_medium=nav&utm_campaign=referral_program';
const REFERRAL_PROGRAM_BANNER_URL =
  'https://supportontelegram.com/referral-program?utm_source=customer_portal&utm_medium=banner&utm_campaign=referral_program&utm_content=management_banner';
const REFERRAL_BANNER_KEY = 'management-referral-program-banner';
const REFERRAL_BANNER_NOTICE_ID = 'referral-banner';

/**
 * Defines the main navigation menu items for the Management interface.
 * Each item represents a route in the management section.
 */
const MANAGEMENT_MENU_ITEMS: MenuItem[] = [
  { to: 'conversations', label: 'Conversations' },
  { to: 'telegram-menu', label: 'Telegram Menu' },
  { to: 'telegram-users', label: 'Telegram Users' },
  { to: 'message-templates', label: 'Message Templates' },
  { to: 'users', label: 'Users' },
  { to: 'reports', label: 'Reports' },
  { to: 'settings', label: 'Settings' },
  { href: REFERRAL_PROGRAM_NAV_URL, label: 'Referral Program' },
] as const;

const Management: FC = () => {
  useDocumentTitle('Management');
  const auth = useAuth();
  const path = '/management';
  const location = useLocation();
  const [messageApi, messageContextHolder] = message.useMessage();
  const userId = auth.getUserId();
  const referralBannerSessionKey = getSessionNoticeKey(REFERRAL_BANNER_NOTICE_ID, userId);
  const [dismissedNoticeKey, setDismissedNoticeKey] = useState<string>();
  const isReferralBannerDismissed =
    dismissedNoticeKey === referralBannerSessionKey || isSessionNoticeDismissed(referralBannerSessionKey);

  useEffect(() => {
    if (isReferralBannerDismissed) {
      messageApi.destroy(REFERRAL_BANNER_KEY);
      return;
    }

    messageApi.open({
      content: (
        <Space align="start">
          <span>
            Know another company using Telegram for customer support?
            <br />
            Earn $250&ndash;$1,000+ for qualified referrals.
            <br />
            <Button
              className={styles.referralBannerAction}
              href={REFERRAL_PROGRAM_BANNER_URL}
              target="_blank"
              rel="noreferrer"
            >
              Learn more and submit a referral
            </Button>
          </span>
          <Button
            aria-label="Close referral banner"
            icon={<CloseOutlined />}
            size="small"
            type="text"
            onClick={() => {
              dismissSessionNotice(referralBannerSessionKey);
              setDismissedNoticeKey(referralBannerSessionKey);
              messageApi.destroy(REFERRAL_BANNER_KEY);
            }}
          />
        </Space>
      ),
      duration: 0,
      key: REFERRAL_BANNER_KEY,
    });

    return () => {
      messageApi.destroy(REFERRAL_BANNER_KEY);
    };
  }, [isReferralBannerDismissed, messageApi, referralBannerSessionKey]);

  return (
    <Layout>
      {messageContextHolder}
      <Layout.Sider className={styles.sider}>
        <Menu
          mode="inline"
          style={{ height: '100%' }}
          selectedKeys={[location.pathname.substring(location.pathname.lastIndexOf(path) + path.length + 1)]}
          items={MANAGEMENT_MENU_ITEMS.map((item) =>
            'href' in item
              ? {
                  label: (
                    <a href={item.href} target="_blank" rel="noreferrer">
                      {item.label}
                    </a>
                  ),
                  key: item.href,
                }
              : {
                  label: (
                    <NavLink to={item.to} viewTransition>
                      {item.label}
                    </NavLink>
                  ),
                  key: item.to,
                },
          )}
        />
      </Layout.Sider>
      <Layout.Content className={styles.content}>
        <ConfigProvider theme={{ token: { zIndexPopupBase: 9999 } }}>
          <Outlet />
        </ConfigProvider>
      </Layout.Content>
    </Layout>
  );
};

export default Management;
